import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Anchor,
  Button,
  Center,
  Loader,
  PasswordInput,
  Stack,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle, IconLock } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  PortalPasswordManagementError,
  PortalPasswordStatus,
  portalPasswordService,
} from "../../services/portalPassword.service";
import { usePortalAuthStore } from "../../store/portalAuth.store";
import { scrollToFirstError } from "../../utils/formValidation";
import {
  PORTAL_PASSWORD_REQUIREMENTS,
  validatePortalPassword,
  validatePortalPasswordConfirmation,
} from "../../utils/portalPasswordValidation";

interface PasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const PASSWORD_STATUS_QUERY_KEY = ["portal", "password-status"];

export function PortalPasswordManager() {
  const queryClient = useQueryClient();
  const portalEmail = usePortalAuthStore((state) => state.email);
  const [submitError, setSubmitError] = useState("");
  const loadPasswordStatus = useCallback(
    () => portalPasswordService.getStatus(),
    []
  );
  const passwordStatusQueryKey = useMemo(
    () => [...PASSWORD_STATUS_QUERY_KEY, portalEmail],
    [portalEmail]
  );
  const statusQuery = useQuery({
    queryKey: passwordStatusQueryKey,
    queryFn: loadPasswordStatus,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
  const hasPassword = useMemo(
    () => statusQuery.data?.hasPassword === true,
    [statusQuery.data?.hasPassword]
  );
  const validateCurrentPassword = useCallback(
    (value: string) =>
      hasPassword && !value ? "Current password is required" : null,
    [hasPassword]
  );
  const validateConfirmedPassword = useCallback(
    (value: string, values: PasswordFormValues) =>
      validatePortalPasswordConfirmation(value, values.newPassword),
    []
  );
  const form = useForm<PasswordFormValues>({
    mode: "uncontrolled",
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validateInputOnBlur: true,
    validate: {
      currentPassword: validateCurrentPassword,
      newPassword: validatePortalPassword,
      confirmPassword: validateConfirmedPassword,
    },
  });

  const managePassword = useCallback(
    (values: PasswordFormValues) =>
      portalPasswordService.manage(
        values.newPassword,
        hasPassword ? values.currentPassword : undefined
      ),
    [hasPassword]
  );
  const handlePasswordSuccess = useCallback(() => {
    queryClient.setQueryData<PortalPasswordStatus>(passwordStatusQueryKey, {
      hasPassword: true,
    });
    form.reset();
    notifications.show({
      color: "green",
      message: hasPassword
        ? "Password changed successfully"
        : "Password created successfully",
    });
  }, [form, hasPassword, passwordStatusQueryKey, queryClient]);
  const handlePasswordError = useCallback(
    (error: Error) => {
      setSubmitError(
        error.message || "We couldn't update your password. Please try again."
      );
      if (
        (error as PortalPasswordManagementError).refreshPasswordStatus === true
      ) {
        void queryClient.invalidateQueries({
          queryKey: passwordStatusQueryKey,
        });
      }
    },
    [passwordStatusQueryKey, queryClient]
  );
  const { isPending, mutate } = useMutation({
    mutationFn: managePassword,
    onSuccess: handlePasswordSuccess,
    onError: handlePasswordError,
  });
  const handleSubmit = useCallback(
    (values: PasswordFormValues) => {
      setSubmitError("");
      mutate(values);
    },
    [mutate]
  );

  const handleValidationError = useCallback(() => {
    scrollToFirstError(form);
  }, [form]);

  if (statusQuery.isLoading) {
    return (
      <Center h={100}>
        <Loader size="sm" />
      </Center>
    );
  }

  if (statusQuery.isError) {
    return (
      <Alert
        icon={<IconAlertCircle size={16} />}
        color="red"
        title="Password settings unavailable"
      >
        We couldn&apos;t load your password settings. Please try again.
      </Alert>
    );
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit, handleValidationError)}>
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {hasPassword
            ? "Change the password you use with your email address."
            : "Create a password so you can sign in with your email address."}
        </Text>

        {hasPassword && (
          <PasswordInput
            withAsterisk
            label="Current password"
            autoComplete="current-password"
            key={form.key("currentPassword")}
            {...form.getInputProps("currentPassword")}
          />
        )}

        <PasswordInput
          withAsterisk
          label={hasPassword ? "New password" : "Password"}
          autoComplete="new-password"
          description={PORTAL_PASSWORD_REQUIREMENTS}
          key={form.key("newPassword")}
          {...form.getInputProps("newPassword")}
        />

        <PasswordInput
          withAsterisk
          label={hasPassword ? "Confirm new password" : "Confirm password"}
          autoComplete="new-password"
          key={form.key("confirmPassword")}
          {...form.getInputProps("confirmPassword")}
        />

        {submitError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            title="Password not updated"
          >
            {submitError}
          </Alert>
        )}

        <Button
          fullWidth
          type="submit"
          loading={isPending}
          leftSection={<IconLock size={16} />}
        >
          {hasPassword ? "Change Password" : "Create Password"}
        </Button>

        {hasPassword && (
          <Text size="sm" c="dimmed" ta="center">
            Forgot your current password?{" "}
            <Anchor component={Link} to="/portal/reset-password?from=account">
              Reset it with an email code
            </Anchor>
          </Text>
        )}
      </Stack>
    </form>
  );
}
